import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, InputBase, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import {
  SHRED_BOARD_FILTERS,
  SHRED_BOARD_PAGE_SIZE,
  SHRED_COLORS,
  SHRED_COPY,
  SHRED_FONTS,
  SHRED_STATUS,
} from '../constants';
import { useFlip } from '../hooks';
import {
  filterEntries,
  formatNumber,
  formatPct,
  getQualificationProgress,
  getSessionsToQualify,
  isRookie,
  pluralize,
} from '../helpers';
import { CountUp, IneligibleChip, LevelChip, MemberAvatar, Panel, Pips, QualifyRing, RankMove, SectionTitle, focusRing, pctColor } from './shared';

const FILTER_LABELS = [
  { id: SHRED_BOARD_FILTERS.ALL, label: 'Everyone' },
  { id: SHRED_BOARD_FILTERS.QUALIFIED, label: 'Ranked' },
  { id: SHRED_BOARD_FILTERS.CHASING, label: 'Still qualifying' },
];

const ROOKIE_FILTER = { id: SHRED_BOARD_FILTERS.ROOKIES, label: SHRED_COPY.BOARD_ROOKIES };

const Chip = ({ active, onClick, children, ...rest }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={active}
    {...rest}
    sx={{
      ...focusRing,
      px: 1.75,
      py: 0.75,
      borderRadius: '999px',
      fontFamily: SHRED_FONTS.body,
      fontWeight: 600,
      fontSize: 13,
      color: active ? SHRED_COLORS.ink : SHRED_COLORS.text,
      background: active ? SHRED_COLORS.heat : 'rgba(255,255,255,0.06)',
      border: `1px solid ${active ? 'transparent' : SHRED_COLORS.line}`,
    }}
  >
    {children}
  </ButtonBase>
);

const rankColor = (rank) => (rank === 1 ? '#ffc233' : rank === 2 ? '#cfd6e0' : rank === 3 ? '#d9894a' : SHRED_COLORS.muted);

const BoardRow = memo(({ entry, mine, maxPct, minSessions, reduced, register }) => {
  const registerRef = register;
  const ranked = entry.qualified;
  const progress = getQualificationProgress(entry, minSessions);
  const need = getSessionsToQualify(entry, minSessions);
  const positive = entry.improvementPct >= 0;
  return (
    <Box
      ref={registerRef(entry.id)}
      data-mine={mine ? 'true' : undefined}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '34px minmax(0,1fr) auto', sm: '48px 34px minmax(0,1fr) auto' },
        alignItems: 'center',
        gap: { xs: 1, sm: 1.5 },
        px: { xs: 1.25, sm: 2 },
        py: 1.1,
        borderBottom: `1px solid ${SHRED_COLORS.line}`,
        borderLeft: `3px solid ${mine ? SHRED_COLORS.gold : 'transparent'}`,
        background: mine
          ? 'linear-gradient(90deg, rgba(255,194,51,0.2), rgba(255,90,31,0.06))'
          : ranked && entry.rank <= 3
          ? 'rgba(255,90,31,0.07)'
          : 'transparent',
        opacity: ranked ? 1 : 0.88,
        position: 'relative',
        willChange: reduced ? 'auto' : 'transform',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        {ranked ? (
          <Typography
            sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 800, fontSize: 24, color: rankColor(entry.rank), lineHeight: 1 }}
            aria-label={`Rank ${entry.rank}`}
          >
            {entry.rank}
          </Typography>
        ) : (
          <QualifyRing done={progress.done} total={progress.total} size={34} label={`${entry.username}: ${progress.done} of ${progress.total} qualifying sessions`} />
        )}
      </Box>
      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{ranked ? <RankMove entry={entry} /> : null}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <MemberAvatar id={entry.id} name={entry.username} size={28} />
          <Box component={RouterLink} to={`/member/${encodeURIComponent(entry.username)}`} sx={{ ...focusRing, textDecoration: 'none', minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontFamily: SHRED_FONTS.body, fontWeight: 600, fontSize: 15, color: mine ? SHRED_COLORS.gold : SHRED_COLORS.text }}
            >
              {entry.username}
              {mine ? ' (you)' : ''}
            </Typography>
          </Box>
          <LevelChip level={entry.level} />
          <IneligibleChip label={entry.ineligibleLabel} />
          {isRookie(entry) && (
            <Box
              component="span"
              sx={{
                flex: 'none',
                fontFamily: SHRED_FONTS.numeric,
                fontSize: 10,
                lineHeight: 1,
                px: 0.75,
                py: '3px',
                borderRadius: '5px',
                color: '#d9bcff',
                background: 'rgba(181,123,255,0.16)',
                border: '1px solid rgba(181,123,255,0.4)',
              }}
            >
              {SHRED_COPY.ROOKIE_TAG.toUpperCase()}
            </Box>
          )}
          {ranked && (
            <Box sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
              <RankMove entry={entry} />
            </Box>
          )}
        </Box>
        <Box sx={{ mt: 0.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: SHRED_COLORS.muted, fontFamily: SHRED_FONTS.body }}>
          {ranked ? (
            <span>
              {pluralize(entry.sessions, 'session')} · avg {formatNumber(entry.avgWork)} vs {formatNumber(entry.baselineAvgWork)} baseline
            </span>
          ) : (
            <>
              <Pips done={progress.done} total={progress.total} />
              <span>{need > 0 ? `${pluralize(need, 'more session')} to rank` : 'Ranking soon'}</span>
            </>
          )}
        </Box>
        {ranked && (
          <Box sx={{ mt: 0.75, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${maxPct > 0 ? (Math.abs(entry.improvementPct) / maxPct) * 100 : 0}%`,
                background: positive ? SHRED_COLORS.heat : SHRED_COLORS.negative,
                transition: reduced ? 'none' : 'width 700ms ease',
              }}
            />
          </Box>
        )}
      </Box>
      <Typography
        sx={{
          fontFamily: SHRED_FONTS.numeric,
          fontWeight: 500,
          fontSize: { xs: 15, sm: 18 },
          color: pctColor(entry.improvementPct),
          fontVariantNumeric: 'tabular-nums',
          textAlign: 'right',
          minWidth: 64,
        }}
      >
        <CountUp value={entry.improvementPct} format={(v) => formatPct(v)} reduced={reduced} />
      </Typography>
    </Box>
  );
});

const Board = ({ entries, challenge, status, me, isMine, reduced, loadError, onRetry, jumpRequest }) => {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState(SHRED_BOARD_FILTERS.ALL);
  const [onlyMe, setOnlyMe] = useState(false);
  const [visible, setVisible] = useState(SHRED_BOARD_PAGE_SIZE);
  const [scrollPending, setScrollPending] = useState(false);
  const [jumping, setJumping] = useState(false);

  const hasRookies = entries.some(isRookie);
  const hasMe = !!me && me.eligible && entries.some(isMine);
  const rows = useMemo(
    () => filterEntries(entries, { query, onlyMe, filter, isMine }),
    // isMine is derived from stable inputs (me / userId); entries identity changes per fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, query, onlyMe, filter, me]
  );
  const shown = rows.slice(0, visible);

  useEffect(() => {
    if (!jumpRequest) {
      return;
    }
    const myIndex = entries.findIndex(isMine);
    if (myIndex < 0) {
      return;
    }
    setQuery('');
    setFilter(SHRED_BOARD_FILTERS.ALL);
    setOnlyMe(false);
    setVisible((count) => Math.max(count, myIndex + 1));
    setJumping(true);
    setScrollPending(true);
    // Only a new jump request should reveal the row; entries and isMine are read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpRequest]);

  useEffect(() => {
    if (!scrollPending) {
      return;
    }
    const row = document.querySelector('#shred-board [data-mine="true"]');
    if (row) {
      row.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      setScrollPending(false);
      setJumping(false);
    }
  }, [scrollPending, shown, reduced]);
  const maxPct = Math.max(0, ...entries.filter((e) => e.qualified).map((e) => Math.abs(e.improvementPct)));
  // Typing in the search box filters rows away; that is not a rank change, so it does not animate.
  const lastQuery = useRef(query);
  const queryChanged = lastQuery.current !== query;
  useEffect(() => {
    lastQuery.current = query;
  });
  const register = useFlip(shown.map((e) => e.id).join('|'), reduced || queryChanged || jumping);

  const emptyCopy =
    entries.length === 0
      ? status === SHRED_STATUS.UPCOMING
        ? SHRED_COPY.EMPTY_UPCOMING
        : SHRED_COPY.EMPTY_LIVE
      : SHRED_COPY.EMPTY_FILTER;

  return (
    <Box component="section" aria-labelledby="shred-board-title" id="shred-board">
      <SectionTitle
        kicker={status === SHRED_STATUS.ENDED ? 'Final standings' : 'Ranked by improvement'}
        title={SHRED_COPY.BOARD_TITLE}
        id="shred-board-title"
        hint={`${challenge.minSessions}+ sessions to rank. Percent change in average work per session vs your own baseline.`}
      />
      <Panel>
        <Box sx={{ p: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', borderBottom: `1px solid ${SHRED_COLORS.line}` }}>
          <InputBase
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setVisible(SHRED_BOARD_PAGE_SIZE);
            }}
            placeholder={SHRED_COPY.BOARD_SEARCH_PLACEHOLDER}
            inputProps={{ 'aria-label': SHRED_COPY.BOARD_SEARCH_LABEL, type: 'search' }}
            sx={{
              flex: '1 1 160px',
              px: 1.5,
              py: 0.25,
              borderRadius: '999px',
              background: 'rgba(255,255,255,0.06)',
              border: `1px solid ${SHRED_COLORS.line}`,
              color: SHRED_COLORS.text,
              fontFamily: SHRED_FONTS.body,
              '&.Mui-focused': { borderColor: SHRED_COLORS.gold },
            }}
          />
          {(hasRookies ? [...FILTER_LABELS, ROOKIE_FILTER] : FILTER_LABELS).map((item) => (
            <Chip key={item.id} active={filter === item.id && !onlyMe} onClick={() => { setFilter(item.id); setOnlyMe(false); }}>
              {item.label}
            </Chip>
          ))}
          {hasMe && (
            <Chip active={onlyMe} onClick={() => setOnlyMe((value) => !value)}>
              {SHRED_COPY.BOARD_ONLY_ME}
            </Chip>
          )}
        </Box>

        {loadError && (
          <Box sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }} role="alert">
            <Typography sx={{ color: SHRED_COLORS.gold, fontFamily: SHRED_FONTS.body, fontSize: 14 }}>{SHRED_COPY.LOAD_ERROR}</Typography>
            <Chip active={false} onClick={onRetry}>{SHRED_COPY.RETRY}</Chip>
          </Box>
        )}

        <Box sx={{ position: 'relative' }}>
          {shown.map((entry) => (
            <BoardRow
              key={entry.id}
              entry={entry}
              mine={isMine(entry)}
              maxPct={maxPct}
              minSessions={challenge.minSessions}
              reduced={reduced}
              register={register}
            />
          ))}
        </Box>

        {rows.length === 0 && !loadError && (
          <Typography sx={{ p: 3, textAlign: 'center', color: SHRED_COLORS.muted, fontFamily: SHRED_FONTS.body }}>{emptyCopy}</Typography>
        )}

        {rows.length > shown.length && (
          <Box sx={{ p: 1.5, textAlign: 'center' }}>
            <Chip active={false} onClick={() => setVisible((count) => count + SHRED_BOARD_PAGE_SIZE)}>
              {SHRED_COPY.BOARD_SHOW_MORE} ({rows.length - shown.length})
            </Chip>
          </Box>
        )}
      </Panel>
    </Box>
  );
};

export default Board;
